<template>
  <!--
    Switch-confirmation modal for the Studio Reading Sessions panel.

    Two cases handled with shared chrome + different copy:

      mode="switch"  — "Switch to <to>?  Your current <from> setup
                        will be saved to Archive so you can restore
                        it later."        [Cancel] [Switch]

      mode="restore" — "Restore <to> from <timestamp>?  Your current
                        <from> configuration will be archived."
                        [Cancel] [Restore]

    All field-level edits already commit on blur, so this modal is a
    context-switch confirm + archive notice — not an unsaved-changes
    triage. See memory: project_variant_switch_save_ux.
  -->
  <Teleport to="body">
    <div class="vsm-backdrop" @click.self="$emit('cancel')" role="dialog" aria-modal="true">
      <div class="vsm-card">
        <h3 class="vsm-title">{{ titleText }}</h3>
        <p class="vsm-body">{{ bodyText }}</p>
        <p v-if="archivedTimestamp" class="vsm-sub">
          Snapshot from {{ archivedTimestamp }}
        </p>
        <div class="vsm-actions">
          <button type="button" class="vsm-btn vsm-cancel" @click="$emit('cancel')">
            Cancel
          </button>
          <button
            type="button"
            class="vsm-btn vsm-confirm"
            :disabled="busy"
            @click="$emit('confirm')"
          >
            {{ busy ? busyLabel : confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // 'switch' (default) — switching from one variant to another.
  // 'restore' — restoring an archived snapshot over the current state.
  mode: { type: String, default: 'switch' },
  fromName: { type: String, required: true },
  toName: { type: String, required: true },
  // Restore-only: when the archived snapshot was taken.
  archivedTimestamp: { type: String, default: '' },
  // Disables the confirm button + swaps its label while the parent
  // is in-flight calling the BE.
  busy: { type: Boolean, default: false },
})
defineEmits(['cancel', 'confirm'])

const titleText = computed(() =>
  props.mode === 'restore'
    ? `Restore ${props.toName}?`
    : `Switch to ${props.toName}?`
)
const bodyText = computed(() => {
  if (props.mode === 'restore') {
    return `Your current ${props.fromName} configuration will be archived.`
  }
  return `Your current ${props.fromName} setup will be saved to Archive so you can restore it later.`
})
const confirmLabel = computed(() =>
  props.mode === 'restore' ? 'Restore' : 'Switch'
)
const busyLabel = computed(() =>
  props.mode === 'restore' ? 'Restoring…' : 'Switching…'
)
</script>

<style scoped>
.vsm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 24px;
  backdrop-filter: blur(4px);
}
.vsm-card {
  background: #1a0c2e;
  border: 1px solid rgba(195, 169, 255, 0.32);
  border-radius: 14px;
  padding: 26px 28px 22px;
  width: 100%;
  max-width: 460px;
  box-shadow:
    0 18px 48px rgba(0, 0, 0, 0.55),
    0 0 32px rgba(201, 169, 78, 0.12);
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.vsm-title {
  font-family: 'Cinzel', serif;
  color: #E8D8FF;
  font-size: 18px;
  margin: 0;
  letter-spacing: 0.02em;
}
.vsm-body {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.55;
  color: rgba(212, 196, 232, 0.85);
  margin: 0;
}
.vsm-sub {
  font-size: 12px;
  color: rgba(195, 169, 255, 0.6);
  margin: 0;
  font-style: italic;
}
.vsm-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 8px;
}
.vsm-btn {
  padding: 9px 18px;
  font-family: 'Cinzel', serif;
  font-size: 13px;
  border-radius: 8px;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: all 0.18s;
}
.vsm-cancel {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.85);
}
.vsm-cancel:hover {
  border-color: rgba(195, 169, 255, 0.6);
  color: #E8D8FF;
}
.vsm-confirm {
  background: linear-gradient(135deg, #E0BC60 0%, #C9A94E 100%);
  border: none;
  color: #1a0c2e;
  font-weight: 700;
}
.vsm-confirm:hover:not(:disabled) {
  box-shadow: 0 0 18px rgba(201, 169, 78, 0.45);
}
.vsm-confirm:disabled {
  opacity: 0.65;
  cursor: wait;
}
</style>
